export class DomAnimation {
  constructor() {
  }

  static KEYFRAMES = {
    SLIDE_OUT_TO_UP: (element) => [
      {
        height: element.offsetHeight + 'px',
      }, {
        height: 0,
      }
    ],
    SLIDE_IN_TO_BOTTOM: (element) => [
      {
        height: 0,
      }, {
        height: element.offsetHeight + 'px',
      }
    ],
    FADE_IN_TO_BOTTOM: () => [
      {
        transform: 'translateY(-20px)',
        opacity: 0,
      },
      {
        transform: 'translateY(0)',
        opacity: 1,
      }
    ],
    FADE_OUT_TO_UP: () => [
      {
        transform: 'translateY(0)',
        opacity: 1,
      },
      {
        transform: 'translateY(-20px)',
        opacity: 0,
      }
    ]
  }
  static KEYFRAMES_OPTIONS = {
    duration: 500,
    easing: "ease-out",
    fill: "forwards",
    iterations: 1,
  };


  /**
   * Static object containing class names used in the animation.
   * @property {string} ITEM - The class name for elements to animate.
   */
  static CLASS_NAMES = {
    TARGET: 'js-animate'
  };


  static TRIGGER_TYPE = {
    ON_CLICK: 'on-click',
    ON_DIALOG_OPEN: 'on-dialog-open',
  };

  static ANIMATION_TYPE = {
    FADE_IN_OUT: 'fadeInOut',
    FADE_IN: 'fadeIn',
    FADE_OUT: 'fadeOut',
    SLIDE_IN_OUT: 'slideInOut',
    SLIDE_IN: 'slideIn',
    SLIDE_OUT: 'slideOut',
  };

  static ANIMATE_SCOPE = {
    SLEF: 'self',
    CHILDREN: 'children'
  }

  /**
   * Initializes the given element with the specified animation type by applying
   * the corresponding CSS styles.
   *
   * @param {HTMLElement} element - The DOM element to initialize with animation.
   * @param {string} animationType - The type of animation to apply.
   *                                  Should be one of the values defined in `DomAnimation.ANIMATION_TYPE`.
   *                                  Supported types:
   *                                  - `DomAnimation.ANIMATION_TYPE.FADE_IN`: Fades in the element.
   *                                  - `DomAnimation.ANIMATION_TYPE.SLIDE_IN`: Slides in the element.
   */
  init(element, animationType) {
    switch (animationType) {
      case DomAnimation.ANIMATION_TYPE.FADE_IN:
        element.style.opacity = '0';
        element.style.transform = 'translateY(-20px)';
        break;

      case DomAnimation.ANIMATION_TYPE.SLIDE_IN:
        element.style.height = '0';
        element.style.opacity = '0';
        break;

      default:
        break;
    }
  }


  /**
   * @method applyFinalStyles
   * Applies the final styles from a keyframes array to a given HTML element.
   *
   * @param {HTMLElement} element - The HTML element to which the styles will be applied.
   * @param {Array<Object>} keyframes - An array of keyframe objects, where each object represents
   *                                    a set of CSS properties and their values. The last keyframe
   *                                    in the array is used to apply the final styles.
   */
  applyFinalStyles(element, keyframes) {
    const finalFrame = keyframes[keyframes.length - 1];
    for (const [prop, value] of Object.entries(finalFrame)) {
      element.style[prop] = value;
    }
  }

  /**
   * Animates an element and optionally its children using Web Animations API.
   *
   * @param {HTMLElement} element - The target element to animate.
   * @param {string|null} [animationType=null] - The type of animation to apply. Must be a valid value from `DomAnimation.ANIMATION_TYPE`.
   * @param {Object} [options={}] - Additional animation options.
   * @param {number} [options.delayStep=300] - Delay increment for animating child elements.
   * @param {string} [options.animateScope] - Scope of animation, e.g., `DomAnimation.ANIMATE_SCOPE.CHILDREN`.
   * @param {Function} [callback] - A callback function to execute after the animation completes.
   *
   * @returns {void}
   *
   * @throws {Error} If the provided animation type is not found in `DomAnimation.ANIMATION_TYPE`.
   *
   * @example
   * const element = document.querySelector('.my-element');
   * animate(element, 'fadeIn', { delayStep: 200 }, () => {
   *   console.log('Animation complete!');
   * });
   */
  animate(element, animationType = null, options = {}, callback) {
    if (!Object.values(DomAnimation.ANIMATION_TYPE).includes(animationType)) {
      console.warn(`Animation type "${animationType}" not found`);
      return;
    }
    const keyframesFn = this.getKeyFrames(animationType);
    if (!keyframesFn) return;

    const isAnimateChildren = element.dataset.animateScope === DomAnimation.ANIMATE_SCOPE.CHILDREN || options.animateScope === DomAnimation.ANIMATE_SCOPE.CHILDREN;
    let children = element.children;
    if (children[0]?.classList.contains('js-ignore')) {
      children = children[0].children;
    }

    const mergedOptions = { ...DomAnimation.KEYFRAMES_OPTIONS, ...options };
    const animations = [];

    element.dataset.animationState = "running";

    const mainAnim = element.animate(keyframesFn(element), mergedOptions);
    animations.push(
      new Promise(resolve => {
        mainAnim.onfinish = () => {
          this.applyFinalStyles(element, keyframesFn(element));
          mainAnim.cancel();
          resolve();
        };
      })
    );

    if (isAnimateChildren && children.length > 0) {
      for (let i = 0; i < children.length; i++) {
        const delay = i * (options.delayStep || 300);
        const child = children[i];
        const childAnim = child.animate(keyframesFn(element), {
          ...mergedOptions,
          delay
        });
        childAnim.pause();
        this.init(child, animationType);
        childAnim.play();
        animations.push(
          new Promise(resolve => {
            childAnim.onfinish = () => {
              this.applyFinalStyles(child, keyframesFn(element));
              childAnim.cancel();
              resolve();
            };
          })
        );
      }
    }

    Promise.all(animations).then(() => {
      if (typeof callback === 'function') {
        callback();
      }
    }).finally(() => {
      element.dataset.animationState = "";
      if ([DomAnimation.ANIMATION_TYPE.SLIDE_IN, DomAnimation.ANIMATION_TYPE.SLIDE_OUT].includes(animationType)) {
        element.style.height = "";
        if (isAnimateChildren && children.length > 0) {
          for (let i = 0; i < children.length; i++) {
            const child = children[i];
            child.style.height = "";
          }
        }
      }
    });
  }

  /**
   * Retrieves the keyframe animation configuration based on the specified animation type.
   *
   * @param {string} type - The type of animation. Expected values are:
   *   - `DomAnimation.ANIMATION_TYPE.FADE_IN`
   *   - `DomAnimation.ANIMATION_TYPE.FADE_OUT`
   *   - `DomAnimation.ANIMATION_TYPE.SLIDE_IN`
   *   - `DomAnimation.ANIMATION_TYPE.SLIDE_OUT`
   *
   * @returns {Object|undefined} The corresponding keyframe animation configuration
   * for the given animation type, or `undefined` if the type is unknown.
   *
   * @throws {void} Logs a warning to the console if an unknown animation type is provided.
   */
  getKeyFrames(type) {
    let keyframe;
    switch (type) {
      case DomAnimation.ANIMATION_TYPE.FADE_IN:
        keyframe = DomAnimation.KEYFRAMES.FADE_IN_TO_BOTTOM;
        break;
      case DomAnimation.ANIMATION_TYPE.FADE_OUT:
        keyframe = DomAnimation.KEYFRAMES.FADE_OUT_TO_UP;
        break;

      case DomAnimation.ANIMATION_TYPE.SLIDE_IN:
        keyframe = DomAnimation.KEYFRAMES.SLIDE_IN_TO_BOTTOM;
        break;
      case DomAnimation.ANIMATION_TYPE.SLIDE_OUT:
        keyframe = DomAnimation.KEYFRAMES.SLIDE_OUT_TO_UP;
        break;

      default:
        console.warn(`[DomAnimation] Unknown animation type: ${type}`);
        break;
    }
    return keyframe;
  }
}
